import React, { useEffect, useRef, useState } from 'react';
import { Paginator } from 'primereact/paginator';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { routes } from 'utils/routes';
import { useNavigate} from 'react-router-dom';
import DeleteItem from 'views/components/delete-modal';
import { SEOHeader } from 'views/components';
import './style.scss'
import { formatDate } from 'utils/common-functions';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { _Object, getStatusLabel, showTostify } from 'utils/interfaces';
import CustomToast from 'views/components/form-inputs/alert';
import { Skeleton } from 'primereact/skeleton';
import { useFormik } from 'formik';
import { blogService } from 'services';

const Blogs = () => {
  const [blogList, setBlogList] = useState<any>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState<_Object>({})
  const [visible, setVisible] = useState(null);
  const toastRef = useRef<any>(null);
  const [filterData, setFilterData] = useState<_Object>({
    page: 1,
    per_page: 10,
    q: "",
    sort: '-created_at',
  })
  const navigate = useNavigate();

  const getBlog = async () => {
    try {
      setLoading(true);
      const data = await blogService.getBlog(filterData);
      setBlogList(data.items);
      setPagination(data.pagination);
    } catch (error) {
      console.error('Error fetching employees:', error);
    } finally {
      setLoading(false);
    }
  };

  const deleteItem = async (itemToDelete: null) => {
    if (!itemToDelete) return;
    try {
      setLoading(true);
      const response = await blogService.deleteBlog(itemToDelete);
      if (!response.error) {
        showTostify(toastRef, 'success', response.message);
        await getBlog();
        setVisible(null); // Close the modal here
      }
    } catch (error: any) {
      showTostify(toastRef, 'error', error.message);
      console.error('Error deleting employee:', error);
    } finally {
      setLoading(false);
    }
  };

  const onPageChange = (event: any) => {
    const currentPage = Math.floor(event.first / event.rows) + 1;

    setFilterData((prev) => ({
      ...prev,
      page: currentPage,
      per_page: event.rows,
    }));
  };

  useEffect(() => {
    getBlog();
  }, [filterData]);

  const formikFilter = useFormik({
    initialValues: filterData,
    enableReinitialize: true,
    onSubmit: (values: _Object) => {
      setFilterData((prev) => ({
        ...prev,
        q: values.q.trim(),
        page: 1
      }));
    }
  });

  return (
    <div className="Blogs">
      <SEOHeader title="Customers" />
      <CustomToast ref={toastRef} />
      <div className="flex justify-content-between align-items-center">
        <div className="p-inputgroup search-bar col-6">
          <form onSubmit={formikFilter.handleSubmit}>
            <div className='form-group mb-0 mr-2'>
              <Button type='submit' className='search-loading bg-transparent p-0 border-none'
                disabled={loading}>
                {loading && <i className="pi pi-spin pi-spinner"
                  style={{ fontSize: '1rem' }}
                >
                </i>}
              </Button>

              <IconField iconPosition="left">
                <InputIcon className="pi pi-search"> </InputIcon>
                <InputText placeholder="Search"
                  name='q'
                  onChange={formikFilter.handleChange}
                  onBlur={formikFilter.handleBlur}
                  value={formikFilter.values.q} />
              </IconField>

              {filterData.q && <Button type='button'
                onClick={() => {
                  setFilterData((prev) => ({
                    ...prev,
                    q: '',
                    page: 1
                  }))
                }}
                severity="secondary" text className='bg-transparent p-0 border-none absolute top-0 right-0 mr-3'>
                <span className="pi pi-times "> </span>
              </Button>}
            </div>
          </form>
        </div>

        <div className="col-4 text-right">
          <Button label="Add New Blog" icon="pi pi-plus" className="primary" onClick={() => navigate(`${routes.blogs}/create`)} />
        </div>
      </div>

      <div className="col">
        <div className="card">
          <table className='w-full'>
            <thead>
              <tr>
                <th>#</th>
                <th>Title</th>
                <th>Slug</th>
                <th>Description</th>
                <th>Short Description</th>
                <th>Date</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {loading && Array.from({ length: 5 }).map((_, index) => (
                <tr key={index}>
                  <td><Skeleton height="2rem" borderRadius="1px" /></td>
                  <td><Skeleton height="2rem" borderRadius="4px" /></td>
                  <td><Skeleton height="2rem" borderRadius="4px" /></td>
                  <td><Skeleton height="2rem" borderRadius="4px" /></td>
                  <td><Skeleton height="2rem" borderRadius="4px" /></td>
                  <td><Skeleton height="2rem" borderRadius="4px" /></td>
                  <td><Skeleton height="2rem" borderRadius="4px" /></td>
                  <td><Skeleton height="2rem" borderRadius="4px" /></td>
                </tr>
              ))}
              {!loading && blogList?.map((item: _Object, i: number) => (
                <tr>
                  <td>{((filterData.page - 1) * filterData.per_page) + (i + 1)}</td>
                  <td>{item?.title || "-"}</td>
                  <td>{item?.slug || "-"}</td>
                  <td>
                    <div dangerouslySetInnerHTML={{ __html: item?.description || "" }}></div>
                  </td>
                  <td>
                    <div dangerouslySetInnerHTML={{ __html: item?.short_description || "" }}></div>
                  </td>
                  <td>{formatDate(item?.created_at) || '-'}</td>
                  <td>{getStatusLabel(item?.status) || '-'}</td>
                  <td>
                    <div>
                      <Button
                        icon="pi pi-pencil"
                        rounded
                        text
                        className="h-auto w-auto bg-transparent p-0 border-noround inline"
                        onClick={() => navigate(`${routes.blogs}/edit/${item.id}`)}
                        style={{ color: '#959CB6' }}
                      />
                      <Button
                        icon="pi pi-trash"
                        onClick={() => {
                          setVisible(item.id);
                        }}
                        rounded
                        text
                        className="h-auto w-auto bg-transparent p-0 border-noround inline"
                        style={{ color: 'var(--torch-red)' }}
                      />
                    </div>
                  </td>
                </tr>
              ))}
              {!loading && blogList?.length === 0 && <tr>
                <td colSpan={11}>No available options</td>
              </tr>
              }
            </tbody>
          </table>
        </div>

        {!loading && pagination?.total_pages > 1 &&
          <Paginator
            first={(filterData.page - 1) * filterData.per_page}
            rows={filterData.per_page}
            totalRecords={pagination.total_items}
            rowsPerPageOptions={[10, 20, 30]}
            onPageChange={onPageChange}
          />
        }
      </div>
      <DeleteItem
        title="Blog"
        visible={visible}
        onHide={() => setVisible(null)}
        deleteItem={() => deleteItem(visible)}
      />
    </div>
  );
};

export default Blogs;
